import { useEffect, useMemo, useState } from 'react';
import { useOutreachHandleImport } from '@discovr/features';
import { creatorKey, handleKey, useCampaignPicks, useDiscoveryCampaign } from '@discovr/features/discovery';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../../components/ui/dialog';
import { Skeleton } from '../../../components/ui/skeleton';
import { POOL_COPY } from '../../../discovery/constants/findCopy';
import { ConfirmDialog } from '../../../discovery/parts/ConfirmDialog';
import { nameOf, outreachHandles, POOL_TABS, poolLists, type PoolCreator, type PoolTab } from '../../../discovery/poolModel';
import { pickMap } from '../../../discovery/results/resultsModel';
import { previewSummary, usePoolOutreach } from '../../../discovery/usePoolOutreach';
import { cn } from '../../../lib/utils';

const COPY = POOL_COPY.outreach;
const IN_OUTREACH = new Set(['already_in_campaign', 'has_price']);

/** Instagram creators with a handle: the only ones outreach can take. */
export const pullableCreators = (pool: readonly PoolCreator[]): PoolCreator[] =>
  pool.filter((creator) => outreachHandles([creator]).length > 0);

interface OutreachPullDialogProps {
  open: boolean;
  campaignId: string;
  onClose: () => void;
}

export function OutreachPullDialog({ open, campaignId, onClose }: OutreachPullDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[80vh] w-[560px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[560px]">
        {open ? <PullBody campaignId={campaignId} onClose={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function PullBody({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const detail = useDiscoveryCampaign(campaignId);
  const picksQuery = useCampaignPicks(campaignId);
  const [tab, setTab] = useState<PoolTab>('shortlisted');
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const outreach = usePoolOutreach(campaignId, onClose);
  const { preview: statusLookup } = useOutreachHandleImport(campaignId);
  const [statuses, setStatuses] = useState<Record<string, string>>({});

  const picks = pickMap(picksQuery.data?.picks);
  const pullable = useMemo(() => pullableCreators(detail.data?.pool ?? []), [detail.data]);
  const { rows } = poolLists(pullable, picks, tab, '');
  const pullableKey = pullable.map(creatorKey).join('|');

  useEffect(() => {
    const handles = outreachHandles(pullable);
    if (!handles.length) return;
    statusLookup.mutate(handles, { onSuccess: (result) => setStatuses(result.statuses ?? {}) });
    // The lookup is keyed on the pool's handles, not on the mutation object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullableKey]);

  const inOutreach = (creator: PoolCreator) =>
    IN_OUTREACH.has(statuses[handleKey(outreachHandles([creator])[0]!)] ?? '');
  const chosen = rows.filter((creator) => selection.has(creatorKey(creator)) && !inOutreach(creator));
  const summary = outreach.previewed ? previewSummary(outreach.previewed) : null;
  const blocked = outreach.blockedReason(chosen);
  const toggle = (creator: PoolCreator) =>
    setSelection((prev) => {
      const next = new Set(prev);
      const key = creatorKey(creator);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const showTab = (next: PoolTab) => {
    setTab(next);
    setSelection(new Set());
  };

  return (
    <>
      <div className="px-6 pt-5">
        <DialogTitle className="text-[18px] font-medium leading-[26px]">Pull creators from Discovery</DialogTitle>
        <DialogDescription className="mt-1 text-[14px] text-foreground-muted">
          Pick Instagram creators from this campaign’s Pool. They land in outreach as Not contacted; no mail is sent.
        </DialogDescription>
        <div className="mt-3 flex gap-2" role="tablist">
          {POOL_TABS.map(({ tab: id, label }) => (
            <Button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              size="sm"
              variant={tab === id ? 'default' : 'outline'}
              onClick={() => showTab(id)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <div className="mt-3 min-h-[160px] flex-1 overflow-y-auto border-t border-stone-200 px-6 py-2">
        {detail.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : detail.isError ? (
          <p className="py-6 text-sm text-foreground-muted">Could not load this campaign’s Pool.</p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-sm text-foreground-muted">
            {tab === 'shortlisted'
              ? 'No Instagram creators are shortlisted yet. Try All.'
              : 'No Instagram creators in this Pool yet.'}
          </p>
        ) : (
          <ul>
            {rows.map((creator) => {
              const taken = inOutreach(creator);
              const key = creatorKey(creator);
              return (
                <li key={key} className={cn('flex items-center gap-3 py-2', taken && 'opacity-60')}>
                  <Checkbox
                    checked={selection.has(key) && !taken}
                    disabled={taken}
                    aria-label={`Select ${nameOf(creator)}`}
                    onCheckedChange={() => toggle(creator)}
                  />
                  <span className="flex-1 truncate text-sm">
                    {nameOf(creator)} <span className="text-foreground-muted">@{outreachHandles([creator])[0]}</span>
                  </span>
                  {taken ? <span className="text-xs text-foreground-muted">Already in outreach</span> : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-stone-200 px-6 py-4">
        {blocked && chosen.length > 0 ? <span className="text-xs text-foreground-muted">{blocked}</span> : null}
        <span className="flex-1" />
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button
          disabled={chosen.length === 0 || blocked !== null}
          loading={outreach.previewing}
          onClick={() => outreach.start(chosen)}
        >
          {chosen.length ? `Add ${chosen.length} to outreach` : 'Add to outreach'}
        </Button>
      </div>
      <ConfirmDialog
        open={summary !== null}
        title={summary?.title ?? ''}
        body={summary?.body ?? ''}
        confirmLabel={COPY.confirm}
        cancelLabel={COPY.cancel}
        confirmVariant="default"
        pending={outreach.committing}
        onConfirm={outreach.confirm}
        onCancel={outreach.cancel}
      />
    </>
  );
}
